export type Quote = {
  text: string;
  author: string;
};

export const copy = {
  hero: {
    clarityWords: ["Clarity", "Insight", "Vision", "Calm"],
    focusWords: ["Focus", "Drive", "Grit", "Flow"],
    adhdWords: ["ADHD", "Insomnia", "Hyperfocus", "Sexual Dysfunction"],
    anxietyWords: [
      "Anxiety",
      "Stress",
      "Panic",
      "Obsessive Compulsive Disorder",
    ],
    depressionWords: [
      "Depression",
      "Sadness",
      "Personality Disorders",
      "Eating Disorders",
    ],
    headline: {
        better: "Better",
        life: "Life",
        starts: "Starts",
        with: "with",
        and: "and"
    },
    subheadline: "Whether you're struggling with",
    subheadlineEnd: ", we're here to support you every step of the way.",
    cta: "Schedule an Appointment"
  },
  insurance: {
    tagline: "The part of the system that actually works.",
    title: "We speak \"Insurance\" (so you don't have to).",
    description: "Let’s be honest, US healthcare is an extreme sport. We handle the red tape and the hold music so you can focus on your progress.",
    providers: [
      "Aetna",
      "Blue Cross Blue Shield",
      "Blue Cross Blue Shield Federal",
      "Cigna",
      "Harvard",
      "Humana",
      "Tricare East",
      "Tricare West",
      "Tufts"
    ]
  },
  about: {
    title: "Welcome to ADHD-AID Psychiatry",
    subtitle: "Comprehensive ADHD Testing",
    description: "At ADHD-AID Psychiatry, we specialize in diagnosing and treating ADHD through evidence-based testing and personalized care. Our two-part testing process provides accurate, objective insights into attention-related challenges, helping you or your loved ones thrive.",
    vision: {
      title: "Care That Actually 'Gets' the ADHD Brain.",
      content: "Most mental health systems feel like a maze. We’ve replaced clinical coldness with Radical Empathy. We know ADHD isn't a lack of willpower. It's a biological reality that requires specialized navigation."
    },
    mission: {
      title: "Tailored to Your Life.",
      content: "We meet you where you are, not where the 'system' thinks you should be. Whether it's precision medication management or evidence-based therapy, our care is designed to fit your unique pace and lifestyle. No 'one-size-fits-all' plans. Just a Board-Certified partnership focused on reducing your daily friction and building long-term momentum."
    }
  },
  externalResources: {
    tagline: "Live Peer Support Groups",
    heading: "Click any card to join live, peer-led online sessions, free, anonymous, and available throughout the week.",
    viewBoardBtn: "View Detailed Board",
    modalTitle: "Support Spaces Directory"
  },
  services: {
    tagline: "Finally, a roadmap you'll actually keep.",
    title: "Care for the \"50 Open Tabs\" Brain.",
    description: "Managing your mental health shouldn't feel like a part-time job. From Gold-Standard Testing to Precision Medication, we handle the clinical \"heavy lifting\" so you can focus on, well... whatever you want.",
    items: [
      {
        id: 1,
        title: "Follow Up Appointment",
        description: "Regular check-ins to monitor your progress and adjust treatment plans as needed."
      },
      {
        id: 2,
        title: "Medication Management (18+)",
        description: "Comprehensive evaluations and personalized medication plans."
      },
      {
        id: 3,
        title: "ADHD Testing",
        description: "Accurate and evidence-based ADHD testing for individuals aged 8 and above."
      },
      {
        id: 4,
        title: "Letter",
        description: "Request official documentation or letters for work, school, or other needs."
      },
      {
        id: 5,
        title: "Therapy",
        description: "Participate in group or individual sessions to share experiences and gain support."
      },
      {
        id: 6,
        title: "Resources",
        description: "Access a variety of resources to support ADHD and mental health management."
      },
      {
        id: 7,
        title: "Collaboration",
        description: "Open to partnerships, referrals, and being in network with fellow practitioners."
      }
    ]
  },
  resources: {
    /** Eyebrow above the main Resources heading */
    tagline: "Downloads, guides, and tools for real life.",
    heroTitle: "Resources",
    heroDescription:
      "Download helpful ADHD forms and resources in PDF. Find templates, guides, and supplemental materials for your treatment",
  },
  booking: {
    tagline: "Simple scheduling. Less friction.",
    title: "Book Your Next Appointment",
    description:
      "Pick a time that works for you and confirm online in a few steps. The calendar shows current openings as our availability updates.",
    urgency: "There is just 1 slot available this month, book now.",
    noSlotsMessage: "No open slots left this month",
    formTitle: "Book your appointment",
    formNoSlots: "There are no open slots left this month.",
    selectSlotHint: "Click a highlighted date to select your slot.",
  },
  features: {
    title: "Why choose us?",
    headline: "Healthcare Designed for the \"First Try.\"",
    description: "The US healthcare system was built for people who remember their passwords and never lose their keys. We're here for the rest of us. At **ADHD-Aid**, we’ve cut out the \"Wall of Awful\" to provide **specialized care that actually works with your brain.**",
    items: [
      {
        title: "In-person & Online Appointments",
        description: "Access expert care from home via secure video."
      },
      {
        title: "Evidence-Based ADHD Testing",
        description: "Available for ages 8+, using advanced assessments."
      },
      {
        title: "Expert Medication Management",
        description: "Guidance from board-certified providers."
      },
      {
        title: "Personalized Treatment Plans",
        description: "Tailored to address specific needs (ADHD, Anxiety, PTSD, etc.)."
      },
      {
        title: "Responsive Staff Support",
        description: "Seamless experience from intake to insurance handling."
      }
    ]
  },
  staff: {
    title: "Meet Brandon Burr, Your Trusted Clinical Prescriber",
    bio: "Brandon is a board-certified Psychiatric Mental Health Nurse Practitioner (PMHNP) and Family Nurse Practitioner (FNP). With extensive experience in psychiatric and emergency medical care, he specializes in ADHD, anxiety, depression, and PTSD. Brandon is passionate about providing personalized, compassionate care to help you achieve your goals.",
    name: "Brandon Burr",
    role: "Clinical Prescriber"
  },
  partners: {
    tagline: "Perks of the ADHD Club.",
    headline: "Friends with (Brain) Benefits.",
    description: "Managing ADHD is a 24/7 job. We’ve teamed up with brands that actually make life easier, providing you with exclusive tools, discounts, and resources to help you stay fueled, focused, and ahead of the curve.",
    orgain: "20% off your first order with exclusive patient discount.",
    helloPrenup: "Save $50 on your prenup with code ADHDAID50."
  },
  socials: {
    tagline: "More dopamine, less doomscrolling.",
    headline: "The Good Kind of Distraction.",
    text: "Most scrolling just makes you feel overwhelmed. Ours makes you feel seen. Follow us for **clinical hacks, zero-judgment tips,** and the occasional reminder that **you’re doing better than you think.**",
    button: "Follow us on Instagram",
    instagramUrl: "https://www.instagram.com/adhdaid.psychiatry/",
  },
  team: {
    tagline: "Real People. Real Expertise.",
    headline: "Clinicians Who Actually \"Get\" It.",
    description: "Tired of providers who lecture you on using a planner? So are we. Meet the **Board-Certified specialists** who swapped clinical coldness for **lived empathy and zero-judgment care.**"
  },
  cta: {
    headline: "Depression? Irritability? ADHD?",
    text: "Make an appointment, get tested, live your best life.",
    button: "Book an Appointment"
  },
  careers: {
    headline: "Careers at ADHD-Aid",
    mission: "Join us in our mission to transform mental health care through innovation and compassionate excellence.",
    teamStatement: "ADHD-Aid is used by patients countrywide. Our team is backed by top-tier providers and experts.",
    note: "If no positions are open, resumes are accepted at careers@adhdaid.com."
  },
  footer: {
    contact: "Have questions or need assistance? Contact us today.",
    locations: ["DC", "Florida", "Hampton Roads VA", "Maryland", "Massachusetts", "Nevada", "Northern Virginia"],
    email: "info@adhd-aid.com",
    phone: "(757) 528-8881",
    officeHours: "Office hours: Monday through Friday, 9 AM to 5 PM",
    clinicalHours: "Clinical hours: Monday through Thursday, 10 AM to 4:30 PM",
    testingHours: "ADHD test hours: Tuesday through Thursday, 11 AM to 2 PM"
  },
  faq: {
    tagline: "For the curious and the overwhelmed.",
    headline: "The \"TL ; DR\" Guide.",
    description: "We know you’ve already got a million tabs open. Here’s the **TL;DR** (Too Long; Didn't Read) version of how we work, what we treat, and how we help you **get your focus back.**"
  },
  cartoons: {
    tagline: "Friday Comics",
    description:
      'Your weekly dose of mental health doodles to make you smile, sigh, or say "Wait, is NP Brandon drawing my thoughts?" Sometimes a little cartoon therapy is exactly what the doctor',
    descriptionEnd: "ordered.",
    exploreBtn: "Browse All Friday Comics",
  },
  blog: {
    sectionTagline: "Articles",
    sectionTitle: "Insights for Your Journey",
    sectionDescription: "Discover strategies, stories, and expert advice to help you navigate life and thrive with ADHD.",
    exploreBtn: "Explore Articles",
    
    heroTagline: "Featured",
    heroTitle: "Mental Health & Wellness Advice",
    heroDescription: "Explore our library of articles written by mental health professionals. Find practical tips, deep insights into ADHD, and effective tools to improve your daily life.",
    
    recentTitle: "Recent blog posts",
    seoTitle: "Articles",
    seoDescription: "Explore articles on ADHD diagnosis, treatment, and management from ADHD-AID Psychiatry."
  },
  universities: {
    tagline: "Rooted in every state we serve",
    title: "Partner Universities Across Our Network",
    description:
      "We collaborate with leading campus institutions in every state we serve, connecting students and families to board-certified ADHD evaluation and psychiatric care closer to home.",
  },
  quotes: [
    {
      text: "Emotions come and go like the weather. Steady yourself with mindfulness and deep breathing.",
      author: "Thich Nhat Hanh",
    },
    {
      text: "Your mind might tell you there's no way forward, but hope is still there, even when you can't see it.",
      author: "John Green",
    },
    {
      text: "Growth happens in the struggle. No one lives a challenge-free life, but facing those challenges can push us toward real change.",
      author: "Neale Donald Walsch",
    },
    {
      text: "Mental health doesn't discriminate. Anyone can be affected. In fact, odds are someone you know is struggling right now.",
      author: "Ruby Wax",
    },
    {
      text: "What we need in mental health isn't silence or stigma. It's open conversations, honesty, and support.",
      author: "Glenn Close",
    },
    {
      text: "One day, you'll look back and see that the toughest years shaped you the most. As long as you're here, there's hope ahead.",
      author: "Sigmund Freud",
    },
    {
      text: "If you don't recognize your own patterns, they'll keep running the show.",
      author: "Carl Jung",
    },
    {
      text: "Mental health isn't a final destination. It's how you handle the journey every day.",
      author: "Noam Shpancer",
    },
    {
      text: "Speak to yourself with the same kindness you'd offer a friend.",
      author: "Brené Brown",
    },
    {
      text: "Even in your darkest seasons, strength is still within you.",
      author: "Albert Camus",
    },
    {
      text: "Most of the things we fear never actually happen, but the stress they create is real.",
      author: "Mark Twain",
    },
  ] satisfies Quote[],
};
